import React, { useCallback } from 'react';
import toast from 'react-hot-toast';

/**
 * SuccessStep - Success state after publishing
 * Shows published URL with action buttons
 */

interface SuccessStepProps {
  url: string;
  onClose: () => void;
}

const SuccessStep: React.FC<SuccessStepProps> = ({ url, onClose }) => {
  const handleCopyUrl = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL copied to clipboard!', {
        className: 'bg-white px-6 py-4 text-dark text-center rounded-lg shadow-lg max-w-xs text-sm',
        duration: 2000,
      });
    } catch (error) {
      toast.error('Failed to copy URL', {
        className: 'bg-white px-6 py-4 text-dark text-center rounded-lg shadow-lg max-w-xs text-sm',
        duration: 2000,
      });
    }
  }, [url]);

  const handleOpenApp = useCallback(() => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [url]);

  return (
    <div className="px-6 py-8 flex flex-col items-center justify-center min-h-[400px]">
      {/* Success Icon with Animation */}
      <div className="relative mb-6">
        <div className="w-16 h-16 rounded-full bg-[#22c55e]/10 flex items-center justify-center animate-bounce-in">
          <svg
            className="w-10 h-10 text-[#22c55e]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
      </div>

      {/* Title with Emoji */}
      <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
        <span>🎉</span>
        <span>App Published!</span>
      </h2>

      {/* Description */}
      <p className="text-white/70 text-center mb-4 text-sm">
        Your app is now live and accessible at:
      </p>

      {/* URL Display */}
      <div className="w-full max-w-lg mb-4">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="block text-center text-[#208AEC] hover:text-[#60a5fa] text-base font-medium hover:underline transition-colors duration-200 break-all px-4"
        >
          {url}
        </a>
      </div>

      {/* Info Box */}
      <div className="w-full max-w-lg bg-white/5 border border-white/10 rounded-lg p-3 mb-4">
        <div className="flex items-start gap-3">
          <svg
            className="w-4 h-4 text-[#208AEC] flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-sm text-white/70">
            DNS propagation may take 5-10 minutes after publishing. Your app will be accessible worldwide once propagation is complete.
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-3 mb-3">
        <button
          onClick={handleCopyUrl}
          className="px-4 py-2 rounded-lg border border-white/20 text-white/90 text-sm hover:bg-white/5 hover:border-white/30 transition-all duration-200 flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
          Copy URL
        </button>
        <button
          onClick={handleOpenApp}
          className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white font-semibold text-sm border border-[#22c55e]/30 shadow-lg shadow-[#22c55e]/20 hover:from-[#16a34a] hover:to-[#15803d] hover:shadow-[#22c55e]/30 transition-all duration-200 flex items-center gap-2"
        >
          Open App
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </button>
      </div>

      {/* Close Link */}
      <button
        onClick={onClose}
        className="text-white/60 hover:text-white text-sm underline transition-colors duration-200"
      >
        Close
      </button>
    </div>
  );
};

export default SuccessStep;

